// src/components/admin/MemeManager.tsx

import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';
import { uploadData } from 'aws-amplify/storage';
import { getUrl } from 'aws-amplify/storage';

const client = generateClient<Schema>();

interface Meme {
  memeId: string;
  imageUrl: string;
  description?: string;
  tone?: string;
  active?: boolean;
  timesShown?: number;
  timesChosen?: number;
}

export default function MemeManager() {
  const [memes, setMemes] = useState<Meme[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [tone, setTone] = useState<'funny' | 'flirty' | 'bold' | 'shy'>('funny');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    fetchMemes();
  }, []);

// Update fetchMemes function to convert paths to URLs
const fetchMemes = async () => {
  try {
    const result = await client.graphql({
      query: /* GraphQL */ `
        query ListMemes {
          listMemeVariants {
            items {
              memeId
              imageUrl
              description
              tone
              active
              timesShown
              timesChosen
            }
          }
        }
      `
    }) as any;

    const items = result.data?.listMemeVariants?.items || [];
    
    // ✅ Convert paths to signed URLs
    const memesWithUrls = await Promise.all(
      items.map(async (meme: Meme) => {
        const urlResult = await getUrl({ path: meme.imageUrl });
        return {
          ...meme,
          imageUrl: urlResult.url.toString()
        };
      })
    );
    
    setMemes(memesWithUrls);
  } catch (error) {
    void ['Failed to fetch memes:', error];
  } finally {
    setLoading(false);
  }
};
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

const handleUpload = async () => {
  if (!selectedFile) {
    alert('Please select an image');
    return;
  }

  setUploading(true);

  try {
    // 1. Upload to S3 via Amplify Storage
    const timestamp = Date.now();
    const sanitizedName = selectedFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const filename = `memes/${timestamp}-${sanitizedName}`;
    
    const operation = uploadData({
      path: filename,
      data: selectedFile,
      options: {
        contentType: selectedFile.type,
      }
    });

const result = await operation.result;

// ✅ Store ONLY the path, not full URL
const storagePath = result.path;

void ['📸 Uploaded to path:', storagePath];

// 2. Create database record with PATH
await client.graphql({
  query: /* GraphQL */ `
    mutation CreateMeme($input: CreateMemeVariantInput!) {
      createMemeVariant(input: $input) {
        memeId
        imageUrl
      }
    }
  `,
  variables: {
    input: {
      memeId: crypto.randomUUID(),
      imageUrl: storagePath, // ✅ Just the path
      description,
      tone,
      active: true,
    }
  }
});

    alert('✅ Meme uploaded successfully!');
    
    // Reset form
    setSelectedFile(null);
    setPreviewUrl(null);
    setDescription('');
    setTone('funny');
    
    // Refresh list
    await fetchMemes();
  } catch (error) {
    void ['Upload failed:', error];
    alert('❌ Upload failed. Check console for details.');
  } finally {
    setUploading(false);
  }
};

  const toggleActive = async (memeId: string, currentActive: boolean) => {
    try {
      await client.graphql({
        query: /* GraphQL */ `
          mutation UpdateMeme($input: UpdateMemeVariantInput!) {
            updateMemeVariant(input: $input) {
              memeId
            }
          }
        `,
        variables: {
          input: {
            memeId,
            active: !currentActive,
          }
        }
      });

      await fetchMemes();
    } catch (error) {
      void ['Toggle failed:', error];
      alert('❌ Failed to toggle meme');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading memes...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Upload Form */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Upload New Meme</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image File
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand file:text-white hover:file:bg-[#d43c3c]"
            />
          </div>

          {previewUrl && (
            <div className="border-2 border-gray-200 rounded-lg p-4">
              <img src={previewUrl} alt="Preview" className="max-h-64 mx-auto" />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Vintage woman looking over shoulder"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tone
            </label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value as any)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="funny">Funny</option>
              <option value="flirty">Flirty</option>
              <option value="bold">Bold</option>
              <option value="shy">Shy</option>
            </select>
          </div>

          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="w-full py-3 bg-brand text-white rounded-lg font-semibold hover:bg-[#d43c3c] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : 'Upload Meme'}
          </button>
        </div>
      </div>

      {/* Memes List */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <h2 className="text-xl font-semibold p-6 border-b">Existing Memes</h2>
        
        {memes.length === 0 ? (
          <p className="text-center text-gray-500 py-12">No memes yet. Upload one above!</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
            {memes.map((meme) => (
              <div key={meme.memeId} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                <img src={meme.imageUrl} alt={meme.description} className="w-full h-48 object-cover" />
                <div className="p-4">
                  <p className="text-sm text-gray-700 mb-2">{meme.description}</p>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs px-2 py-1 rounded ${
                      meme.tone === 'funny' ? 'bg-yellow-100 text-yellow-800' :
                      meme.tone === 'flirty' ? 'bg-pink-100 text-pink-800' :
                      meme.tone === 'bold' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {meme.tone}
                    </span>
                    <span className="text-xs text-gray-500">
                      {meme.timesShown || 0} shown / {meme.timesChosen || 0} chosen
                    </span>
                  </div>
                  <button
                    onClick={() => toggleActive(meme.memeId, meme.active || false)}
                    className={`w-full py-2 rounded font-medium ${
                      meme.active
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {meme.active ? '✅ Active' : '❌ Inactive'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}